import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../services/token.service';
import { AccountRole } from '../types/dtos/account.dto';

/**
 * A route guard function that checks for the presence of an access token
 * to determine whether the user is authorized to access certain routes.
 *
 * If an access token is found, access is granted. Otherwise, the user is redirected
 * to the login page.
 *
 * Dependencies:
 * - Uses `TokenService` to retrieve the access token.
 * - Uses `Router` to handle navigation when access is denied.
 *
 * @type {CanActivateFn}
 * @returns {boolean | UrlTree} Returns `true` if access is granted, or a `UrlTree` redirecting to the login page if
 *   not.
 */
export const authGuard: CanActivateFn = (): boolean | UrlTree => {
  const tokenService = inject(TokenService);
  const router = inject(Router);

  const accessToken = tokenService.getAccessToken();
  const refreshToken = tokenService.getRefreshToken();

  if (accessToken || refreshToken) {
    return true;
  }

  return router.createUrlTree(['/login']);
};

const accessRole = (role: AccountRole): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.canAccess(role)) {
    return true;
  }

  return router.createUrlTree(['/']);
}

export const accessAdminGuard: CanActivateFn = (): boolean | UrlTree => {
  return accessRole(AccountRole.ADMIN);
}

export const accessSuperAdminGuard: CanActivateFn = (): boolean | UrlTree => {
  return accessRole(AccountRole.ADMIN);
}